import forEach from "lodash/forEach";
import fromPairs from "lodash/fromPairs";
import groupBy from "lodash/groupBy";
import isUndefined from "lodash/isUndefined";
import type { Dispatch, Store } from "redux";

import {
	type DeletedEvent,
	type GlobalStoreEvent,
	type GlobalStoreSnapshot,
	type Node,
	type NodeId,
	type QueryView,
	Type,
	WebSocketStreamType,
} from "~/models/dto";
import { Logger } from "~/tools/logger";
import { SparseArray } from "~/tools/sparse-array";

import { selectWebUiConfig } from "../app/state/config/ConfigSelectors";
import { dispatchActionOnArrayChangedEvent } from "../app/state/global/ArrayChangedEventToAction";
import { getGlobalChangedActionSpecs } from "../app/state/global/GlobalChangedActions";
import type { RootState } from "../app/state/RootState";
import { defaultNotificationEpicErrorHandler } from "../app/state/StateUtil";
import { streamApiService } from "../backend";
import { IllegalStateError } from "../common/errors";

import { type NodeTypes, mergeGlobalStoreNodes, optionalFieldFor } from "./GlobalStoreHelper";

export class GlobalStore {
	public static init(store: Store<RootState>): Promise<void> {
		const globalStore = new GlobalStore();

		globalStore.log.debug("(Re)loading global model using stream.");

		globalStore.connectToReduxStore(store.dispatch);

		return new Promise((resolve) => {
			const config = selectWebUiConfig(store.getState());
			streamApiService
				.startStream(
					WebSocketStreamType.GlobalStoreSnapshot,
					{
						_type: Type.GlobalStoreRequest,
						typeName: "_",
					},
					config,
				)
				.subscribe((dto) => {
					globalStore.handleUpdate(dto);
					if (dto._type === Type.GlobalStoreSnapshot) {
						resolve();
					}
				}, defaultNotificationEpicErrorHandler(globalStore.log));
		});
	}

	private readonly log = Logger.namespace("GlobalStore");

	private readonly nodeTypes: NodeTypes = fromPairs([[Type.QueryView, SparseArray.empty<NodeId, QueryView>()]]);

	private constructor() {}

	/**
	 * For all known global changed actions this will subscribe to the globalstore's node arrays and
	 * dispatch redux actions every time something changes in them.
	 */
	private connectToReduxStore(dispatch: Dispatch): void {
		getGlobalChangedActionSpecs().forEach(({ nodeType, factory }) => {
			const nodeArray = this.nodeTypes[nodeType];

			if (nodeArray) {
				const eventStream = nodeArray.eventStream();
				dispatchActionOnArrayChangedEvent(factory, eventStream, dispatch);
			}
		});
	}

	private handleUpdate(dto: GlobalStoreEvent | GlobalStoreSnapshot): void {
		switch (dto._type) {
			case Type.GlobalStoreSnapshot:
				this.handleSnapshotUpdate(dto.node);
				break;
			case Type.GlobalStoreEvent:
				this.handleSingleNodeUpdate(dto);
				break;
		}
	}

	/**
	 * The global store can be reloaded multiple times due to reconnection of the websocket.
	 * Due to that handleSnapshotUpdate implements fine grained update procedure
	 * - update node only if node timestamp is newer than the one in the store
	 * - remove node only if the node is not in the new snapshot anymore.
	 * @param nodes
	 */
	private handleSnapshotUpdate(nodes: Array<Node>) {
		const groupedByType = groupBy(nodes, (node) => node._type);
		forEach(this.nodeTypes, (nodeTypeSpec, type) => {
			const newNodesOfType = groupedByType[type];
			const oldNodesOfType = nodeTypeSpec!;
			if (isUndefined(newNodesOfType)) {
				oldNodesOfType.clear();
			} else {
				this.internalMergeAndRemove(newNodesOfType, oldNodesOfType);
			}
		});
	}

	private internalMergeAndRemove<T extends Node = Node>(
		newNodesOfType: Array<T>,
		oldNodesOfType: SparseArray<NodeId, T>,
	) {
		const newNodeIds = newNodesOfType.map((node) => node.id!);
		const nodesToRemoveOfType = oldNodesOfType.values().filter((currentNode) => !newNodeIds.includes(currentNode.id!));

		mergeGlobalStoreNodes(newNodesOfType, this.nodeTypes);
		nodesToRemoveOfType.forEach((nodeToRemove) => {
			this.removeNodeInternal(nodeToRemove._type, nodeToRemove.id!);
		});
	}

	private handleSingleNodeUpdate(storeEvent: GlobalStoreEvent) {
		switch (storeEvent.event._type) {
			case Type.CreatedEvent:
			case Type.UpdatedEvent:
				this.log.debug("Received global store node merge: ", storeEvent.node);
				mergeGlobalStoreNodes(storeEvent.node ? [storeEvent.node] : [], this.nodeTypes);
				break;
			case Type.EmbeddingParentUpdatedEvent:
				this.log.debug("Received embedding parent event: ", storeEvent.node);

				if (storeEvent.node) {
					this.replaceNode(storeEvent.node);
				}
				break;
			case Type.DeletedEvent: {
				const deleteEvent = <DeletedEvent>storeEvent.event;
				this.log.debug("Received global store node delete: ", deleteEvent.nodeType, deleteEvent.elementId);
				this.removeNodeInternal(deleteEvent.nodeType, deleteEvent.elementId);
				break;
			}
			default:
				this.log.warn(
					`GlobalStore single node update received but not processed: ${storeEvent.event._type}. Unrecognized event type.`,
				);
		}
	}

	private replaceNode(newNode: Node) {
		const field = optionalFieldFor(newNode, this.nodeTypes);

		if (field) {
			field.merge(newNode.id!, newNode);
		} else {
			this.log.warn(`Got unexpected type '${newNode._type}' while merging global node.`);
		}
	}

	private removeNodeInternal(nodeType: string, nodeId: NodeId) {
		const nodeArray = this.nodeTypes[nodeType];

		if (!nodeArray) {
			throw new IllegalStateError(`Cannot find node descriptor for node of type: '${nodeType}'.`);
		}

		nodeArray.remove(nodeId);
	}
}
